/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Type} from '@angular/core';

let /** @type {?} */ _DOM: DomAdapter = /** @type {?} */(( null));
/**
 * @return {?}
 */
export function getDOM() {
  return _DOM;
}
/**
 * @param {?} adapter
 * @return {?}
 */
export function setDOM(adapter: DomAdapter) {
  _DOM = adapter;
}
/**
 * @param {?} adapter
 * @return {?}
 */
export function setRootDomAdapter(adapter: DomAdapter) {
  if (!_DOM) {
    _DOM = adapter;
  }
}
/**
 * Provides DOM operations in an environment-agnostic way.
 * 
 * \@security Tread carefully! Interacting with the DOM directly is dangerous and
 * can introduce XSS risks.
 * @abstract
 */
export abstract class DomAdapter {
public resourceLoaderType: Type<any> = /** @type {?} */(( null));
/**
 * @abstract
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
hasProperty(element: any, name: string) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: Element, name: string, value: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
getProperty(el: Element, name: string) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} methodName
 * @param {?} args
 * @return {?}
 */
invoke(el: Element, methodName: string, args: any[]) {}
/**
 * @abstract
 * @param {?} error
 * @return {?}
 */
logError(error: any) {}
/**
 * @abstract
 * @param {?} error
 * @return {?}
 */
log(error: any) {}
/**
 * @abstract
 * @param {?} error
 * @return {?}
 */
logGroup(error: any) {}
/**
 * @abstract
 * @return {?}
 */
logGroupEnd() {}
/**
 * Maps attribute names to their corresponding property names for cases
 * where attribute name doesn't match property name.
 * @return {?}
 */
get attrToPropMap(): {[key: string]: string} { return this._attrToPropMap; };
/**
 * @param {?} value
 * @return {?}
 */
set attrToPropMap(value: {[key: string]: string}) { this._attrToPropMap = value; };
/**
 * \@internal
 */
_attrToPropMap: {[key: string]: string};
/**
 * @abstract
 * @param {?} nodeA
 * @param {?} nodeB
 * @return {?}
 */
contains(nodeA: any, nodeB: any) {}
/**
 * @abstract
 * @param {?} templateHtml
 * @return {?}
 */
parse(templateHtml: string) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} selector
 * @return {?}
 */
querySelector(el: any, selector: string) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} selector
 * @return {?}
 */
querySelectorAll(el: any, selector: string) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} evt
 * @param {?} listener
 * @return {?}
 */
on(el: any, evt: any, listener: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} evt
 * @param {?} listener
 * @return {?}
 */
onAndCancel(el: any, evt: any, listener: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} evt
 * @return {?}
 */
dispatchEvent(el: any, evt: any) {}
/**
 * @abstract
 * @param {?} eventType
 * @return {?}
 */
createMouseEvent(eventType: any) {}
/**
 * @abstract
 * @param {?} eventType
 * @return {?}
 */
createEvent(eventType: string) {}
/**
 * @abstract
 * @param {?} evt
 * @return {?}
 */
preventDefault(evt: any) {}
/**
 * @abstract
 * @param {?} evt
 * @return {?}
 */
isPrevented(evt: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getInnerHTML(el: any) {}
/**
 * Returns content if el is a <template> element, null otherwise.
 * @abstract
 * @param {?} el
 * @return {?}
 */
getTemplateContent(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getOuterHTML(el: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
nodeName(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
nodeValue(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
type(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
content(node: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
firstChild(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
nextSibling(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
parentElement(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
childNodes(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
childNodesAsList(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
clearNodes(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
appendChild(el: any, node: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
removeChild(el: any, node: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} newNode
 * @param {?} oldNode
 * @return {?}
 */
replaceChild(el: any, newNode: any, oldNode: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
remove(el: any) {}
/**
 * @abstract
 * @param {?} parent
 * @param {?} ref
 * @param {?} node
 * @return {?}
 */
insertBefore(parent: any, ref: any, node: any) {}
/**
 * @abstract
 * @param {?} parent
 * @param {?} ref
 * @param {?} nodes
 * @return {?}
 */
insertAllBefore(parent: any, ref: any, nodes: any) {}
/**
 * @abstract
 * @param {?} parent
 * @param {?} el
 * @param {?} node
 * @return {?}
 */
insertAfter(parent: any, el: any, node: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setInnerHTML(el: any, value: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getText(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setText(el: any, value: string) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getValue(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setValue(el: any, value: string) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getChecked(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @param {?} value
 * @return {?}
 */
setChecked(el: any, value: boolean) {}
/**
 * @abstract
 * @param {?} text
 * @return {?}
 */
createComment(text: string) {}
/**
 * @abstract
 * @param {?} html
 * @return {?}
 */
createTemplate(html: any) {}
/**
 * @abstract
 * @param {?} tagName
 * @param {?=} doc
 * @return {?}
 */
createElement(tagName: any, doc?: any) {}
/**
 * @abstract
 * @param {?} ns
 * @param {?} tagName
 * @param {?=} doc
 * @return {?}
 */
createElementNS(ns: string, tagName: string, doc?: any) {}
/**
 * @abstract
 * @param {?} text
 * @param {?=} doc
 * @return {?}
 */
createTextNode(text: string, doc?: any) {}
/**
 * @abstract
 * @param {?} attrName
 * @param {?} attrValue
 * @param {?=} doc
 * @return {?}
 */
createScriptTag(attrName: string, attrValue: string, doc?: any) {}
/**
 * @abstract
 * @param {?} css
 * @param {?=} doc
 * @return {?}
 */
createStyleElement(css: string, doc?: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
createShadowRoot(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getShadowRoot(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getHost(el: any) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getDistributedNodes(el: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
clone /*<T extends Node>*/ (node: Node) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getElementsByClassName(element: any, name: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getElementsByTagName(element: any, name: string) {}
/**
 * @abstract
 * @param {?} element
 * @return {?}
 */
classList(element: any) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
addClass(element: any, className: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
removeClass(element: any, className: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} className
 * @return {?}
 */
hasClass(element: any, className: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} styleName
 * @param {?} styleValue
 * @return {?}
 */
setStyle(element: any, styleName: string, styleValue: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} styleName
 * @return {?}
 */
removeStyle(element: any, styleName: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} styleName
 * @return {?}
 */
getStyle(element: any, styleName: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} styleName
 * @param {?=} styleValue
 * @return {?}
 */
hasStyle(element: any, styleName: string, styleValue?: string) {}
/**
 * @abstract
 * @param {?} element
 * @return {?}
 */
tagName(element: any) {}
/**
 * @abstract
 * @param {?} element
 * @return {?}
 */
attributeMap(element: any) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
hasAttribute(element: any, attribute: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} ns
 * @param {?} attribute
 * @return {?}
 */
hasAttributeNS(element: any, ns: string, attribute: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
getAttribute(element: any, attribute: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} ns
 * @param {?} attribute
 * @return {?}
 */
getAttributeNS(element: any, ns: string, attribute: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setAttribute(element: any, name: string, value: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} ns
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setAttributeNS(element: any, ns: string, name: string, value: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} attribute
 * @return {?}
 */
removeAttribute(element: any, attribute: string) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} ns
 * @param {?} attribute
 * @return {?}
 */
removeAttributeNS(element: any, ns: string, attribute: string) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
templateAwareRoot(el: any) {}
/**
 * @abstract
 * @return {?}
 */
createHtmlDocument() {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
getBoundingClientRect(el: any) {}
/**
 * @abstract
 * @param {?} doc
 * @return {?}
 */
getTitle(doc: Document) {}
/**
 * @abstract
 * @param {?} doc
 * @param {?} newTitle
 * @return {?}
 */
setTitle(doc: Document, newTitle: string) {}
/**
 * @abstract
 * @param {?} n
 * @param {?} selector
 * @return {?}
 */
elementMatches(n: any, selector: string) {}
/**
 * @abstract
 * @param {?} el
 * @return {?}
 */
isTemplateElement(el: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
isTextNode(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
isCommentNode(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
isElementNode(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
hasShadowRoot(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
isShadowRoot(node: any) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
importIntoDoc /*<T extends Node>*/ (node: Node) {}
/**
 * @abstract
 * @param {?} node
 * @return {?}
 */
adoptNode /*<T extends Node>*/ (node: Node) {}
/**
 * @abstract
 * @param {?} element
 * @return {?}
 */
getHref(element: any) {}
/**
 * @abstract
 * @param {?} event
 * @return {?}
 */
getEventKey(event: any) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} baseUrl
 * @param {?} href
 * @return {?}
 */
resolveAndSetHref(element: any, baseUrl: string, href: string) {}
/**
 * @abstract
 * @return {?}
 */
supportsDOMEvents() {}
/**
 * @abstract
 * @return {?}
 */
supportsNativeShadowDOM() {}
/**
 * @abstract
 * @param {?} doc
 * @param {?} target
 * @return {?}
 */
getGlobalEventTarget(doc: Document, target: string) {}
/**
 * @abstract
 * @return {?}
 */
getHistory() {}
/**
 * @abstract
 * @return {?}
 */
getLocation() {}
/**
 * @abstract
 * @param {?} doc
 * @return {?}
 */
getBaseHref(doc: Document) {}
/**
 * @abstract
 * @return {?}
 */
resetBaseElement() {}
/**
 * @abstract
 * @return {?}
 */
getUserAgent() {}
/**
 * @abstract
 * @param {?} element
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setData(element: any, name: string, value: string) {}
/**
 * @abstract
 * @param {?} element
 * @return {?}
 */
getComputedStyle(element: any) {}
/**
 * @abstract
 * @param {?} element
 * @param {?} name
 * @return {?}
 */
getData(element: any, name: string) {}
/**
 * @abstract
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setGlobalVar(name: string, value: any) {}
/**
 * @abstract
 * @return {?}
 */
supportsWebAnimation() {}
/**
 * @abstract
 * @return {?}
 */
performanceNow() {}
/**
 * @abstract
 * @return {?}
 */
getAnimationPrefix() {}
/**
 * @abstract
 * @return {?}
 */
getTransitionEnd() {}
/**
 * @abstract
 * @return {?}
 */
supportsAnimation() {}
/**
 * @abstract
 * @return {?}
 */
supportsCookies() {}
/**
 * @abstract
 * @param {?} name
 * @return {?}
 */
getCookie(name: string) {}
/**
 * @abstract
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setCookie(name: string, value: string) {}
}

function DomAdapter_tsickle_Closure_declarations() {
/** @type {?} */
DomAdapter.prototype.resourceLoaderType;
/**
 * \@internal
 * @type {?}
 */
DomAdapter.prototype._attrToPropMap;
}

